import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MapPin, Building2, Users, RefreshCcw, Info } from "lucide-react";

// Fix default marker icons for CRA/webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const npIcon = new L.DivIcon({
  className: "np-marker",
  html: `<div style="width:14px;height:14px;border-radius:50%;background:#4A6B5D;border:3px solid white;box-shadow:0 0 0 1px #4A6B5D"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});
const facIcon = new L.DivIcon({
  className: "fac-marker",
  html: `<div style="width:10px;height:10px;border-radius:2px;background:#B08A55;border:2px solid white;box-shadow:0 0 0 1px #B08A55"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});
const facUnassignedIcon = new L.DivIcon({
  className: "fac-marker-unassigned",
  html: `<div style="width:10px;height:10px;border-radius:2px;background:#C17D6E;border:2px solid white;box-shadow:0 0 0 1px #C17D6E"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// Simple in-memory geocode cache across component lifetime
const geoCache = new Map();

async function geocodeOne(query) {
  if (!query || !query.trim()) return null;
  const key = query.trim().toLowerCase();
  if (geoCache.has(key)) return geoCache.get(key);
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`, {
      headers: { "Accept": "application/json" },
    });
    const data = await res.json();
    if (Array.isArray(data) && data[0]) {
      const pt = { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
      geoCache.set(key, pt);
      return pt;
    }
    geoCache.set(key, null);
    return null;
  } catch {
    return null;
  }
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 11 });
  }, [map, points]);
  return null;
}

export default function CoverageMapPage() {
  const [nps, setNps] = useState([]);
  const [facs, setFacs] = useState([]);
  const [npCoords, setNpCoords] = useState({});
  const [facCoords, setFacCoords] = useState({});
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => { (async () => {
    try {
      const [n, f] = await Promise.all([api.get("/nps"), api.get("/facilities")]);
      setNps(n.data || []); setFacs(f.data || []);
      // Pre-populate map with stored coords immediately (no wait)
      const npMap = {}; const facMap = {};
      (n.data || []).forEach((x) => { if (x.lat != null && x.lng != null) npMap[x.id] = { lat: x.lat, lon: x.lng }; });
      (f.data || []).forEach((x) => { if (x.lat != null && x.lng != null) facMap[x.id] = { lat: x.lat, lon: x.lng }; });
      setNpCoords(npMap); setFacCoords(facMap);
    } catch { toast.error("Could not load NPs / facilities"); }
  })(); }, []);

  const geocodeAll = async () => {
    setLoading(true);
    // Prefer stored coords first
    const npMap = {}; const facMap = {};
    nps.forEach((n) => { if (n.lat != null && n.lng != null) npMap[n.id] = { lat: n.lat, lon: n.lng }; });
    facs.forEach((f) => { if (f.lat != null && f.lng != null) facMap[f.id] = { lat: f.lat, lon: f.lng }; });
    // Ask backend to fill in any missing ones (persisted), then reload
    try { await api.post("/geocode/refresh"); } catch { /* non-fatal, fall back to client geocoding */ }
    try {
      const [n, f] = await Promise.all([api.get("/nps"), api.get("/facilities")]);
      const nRefreshed = n.data || []; const fRefreshed = f.data || [];
      setNps(nRefreshed); setFacs(fRefreshed);
      nRefreshed.forEach((x) => { if (x.lat != null && x.lng != null) npMap[x.id] = { lat: x.lat, lon: x.lng }; });
      fRefreshed.forEach((x) => { if (x.lat != null && x.lng != null) facMap[x.id] = { lat: x.lat, lon: x.lng }; });
    } catch { /* ignore */ }
    // Any still missing → client-side geocode as fallback
    const tasks = [];
    nps.forEach((n) => { if (!npMap[n.id] && (n.address || n.city)) tasks.push({ kind: "np", id: n.id, q: [n.address, n.city, n.state, n.zip].filter(Boolean).join(", ") }); });
    facs.forEach((f) => { if (!facMap[f.id]) { const q = [f.address, f.city, f.state, f.zip].filter(Boolean).join(", "); if (q.trim()) tasks.push({ kind: "fac", id: f.id, q }); }});
    setProgress({ done: 0, total: tasks.length });
    for (let i = 0; i < tasks.length; i++) {
      const t = tasks[i];
      // eslint-disable-next-line no-await-in-loop
      const pt = await geocodeOne(t.q);
      if (pt) { if (t.kind === "np") npMap[t.id] = pt; else facMap[t.id] = pt; }
      setProgress({ done: i + 1, total: tasks.length });
      if (!geoCache.has(t.q.trim().toLowerCase())) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 1100));
      }
    }
    setNpCoords(npMap);
    setFacCoords(facMap);
    setLoading(false);
  };

  const points = useMemo(() => [
    ...Object.values(npCoords),
    ...Object.values(facCoords),
  ], [npCoords, facCoords]);

  const lines = useMemo(() => {
    const out = [];
    facs.forEach((f) => {
      const facPt = facCoords[f.id];
      const npPt = f.np_id ? npCoords[f.np_id] : null;
      if (facPt && npPt) out.push({ id: f.id, positions: [[npPt.lat, npPt.lon], [facPt.lat, facPt.lon]] });
    });
    return out;
  }, [facs, facCoords, npCoords]);

  const hasAnyCoord = points.length > 0;
  const withAddress = nps.filter((n) => (n.address || "").trim()).length;
  const facsWithLoc = facs.filter((f) => [f.address, f.city, f.state].some((v) => (v || "").trim())).length;

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Tools</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2 flex items-center gap-3">
            <MapPin className="h-7 w-7 text-[#4A6B5D]"/> Coverage Map
          </h1>
          <p className="text-sm text-[#5C5850] mt-1.5 max-w-2xl">
            NPs shown as green dots, facilities as amber squares, unassigned facilities as red squares. Lines connect each facility to its assigned NP.
          </p>
        </div>
        <Button onClick={geocodeAll} disabled={loading} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="map-geocode-btn">
          {loading ? <><RefreshCcw className="h-4 w-4 mr-2 animate-spin"/> Geocoding… {progress.done}/{progress.total}</> : <><MapPin className="h-4 w-4 mr-2"/> {hasAnyCoord ? "Refresh map" : "Load map"}</>}
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <StatCard icon={Users} label="NPs" value={`${withAddress}/${nps.length}`} note="with address" />
        <StatCard icon={Building2} label="Facilities" value={`${facsWithLoc}/${facs.length}`} note="with location" />
        <StatCard icon={MapPin} label="On map" value={points.length} note="geocoded" />
        <StatCard icon={MapPin} label="Links drawn" value={lines.length} note="NP → facility" />
      </div>

      <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md overflow-hidden" style={{ height: "560px" }}>
        {!hasAnyCoord ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <MapPin className="h-8 w-8 text-[#8A847A] opacity-60"/>
            <p className="text-sm text-[#8A847A] mt-3">Tap "Load map" to geocode every NP + facility address and plot them.</p>
            <p className="text-[11px] text-[#8A847A] mt-1.5 max-w-md flex items-start gap-1.5"><Info className="h-3 w-3 mt-0.5 flex-shrink-0"/> First run is slow — OpenStreetMap limits to ~1 lookup / second. Results are cached in this session.</p>
          </div>
        ) : (
          <MapContainer center={[39.5, -98.35]} zoom={4} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={points}/>
            {Object.entries(npCoords).map(([id, pt]) => {
              const n = nps.find((x) => x.id === id);
              return (
                <Marker key={`np-${id}`} position={[pt.lat, pt.lon]} icon={npIcon}>
                  <Popup>
                    <div className="text-xs">
                      <div className="text-[10px] uppercase tracking-[0.06em] text-[#4A6B5D]">NP</div>
                      <div className="font-semibold">{n?.name}</div>
                      <div className="text-[#5C5850] mt-1">{n?.address || "—"}</div>
                      <Link to={`/nps/${id}`} className="text-[#4A6B5D] hover:underline block mt-1.5">Open profile →</Link>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {Object.entries(facCoords).map(([id, pt]) => {
              const f = facs.find((x) => x.id === id);
              const isUnassigned = !f?.np_id;
              return (
                <Marker key={`fac-${id}`} position={[pt.lat, pt.lon]} icon={isUnassigned ? facUnassignedIcon : facIcon}>
                  <Popup>
                    <div className="text-xs">
                      <div className={`text-[10px] uppercase tracking-[0.06em] ${isUnassigned ? "text-[#C17D6E]" : "text-[#B08A55]"}`}>{isUnassigned ? "Unassigned facility" : "Facility"}</div>
                      <div className="font-semibold">{f?.name}</div>
                      <div className="text-[#5C5850] mt-1">{[f?.city, f?.state].filter(Boolean).join(", ")}</div>
                      <div className="text-[#8A847A] mt-1">NP: {f?.np?.name || "unassigned"}</div>
                      <Link to={`/facilities/${id}`} className="text-[#4A6B5D] hover:underline block mt-1.5">Open folder →</Link>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {lines.map((l) => (
              <Polyline key={l.id} positions={l.positions} pathOptions={{ color: "#4A6B5D", weight: 1.5, opacity: 0.5, dashArray: "4 4" }} />
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, note }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-[0.06em] text-[#8A847A] flex items-center gap-1.5">
        <Icon className="h-3 w-3"/> {label}
      </div>
      <div className="mt-1 heading text-2xl font-bold">{value}</div>
      <div className="text-[10px] text-[#8A847A]">{note}</div>
    </div>
  );
}
